<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h2 class="modal-title">
          {{ $t("message.address_form.edit_address") }}
        </h2>
        <button @click="$emit('close')" class="close-button">×</button>
      </div>

      <!-- Disabled Address Warning Banner -->
      <div v-if="formData.disabled === 1" class="disabled-warning">
        <svg
          class="warning-icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <div>
          <strong>{{
            $t("message.address_form.address_disabled_warning")
          }}</strong>
          <p class="text-sm mt-1">
            {{ $t("message.address_form.address_disabled_description") }}
          </p>
        </div>
      </div>

      <div class="modal-body">
        <div v-if="loading" class="loading-container">
          <baseMiniSpinner></baseMiniSpinner>
        </div>

        <form v-else @submit.prevent="saveAddress" class="address-form">
          <div v-if="error" class="error-message mb-4">
            {{ error }}
          </div>
          <!-- Name -->
          <div class="form-group">
            <label class="form-label">{{
              $t("message.address_form.name")
            }}</label>
            <input
              :value="formData.name"
              type="text"
              class="form-input bordered bg-gray-100 cursor-not-allowed"
              readonly
            />
            <p class="text-xs text-gray-500 mt-1">
              {{ $t("message.address_form.name_locked_hint") }}
            </p>
          </div>

          <!-- Street 1 with Google Autocomplete -->
          <div class="form-group">
            <GoogleAddressSearch
              v-model="formData.street1"
              :label="$t('message.address_form.street1') + ' *'"
              :placeholder="$t('message.address_form.start_typing_address')"
              @address-selected="handleGoogleAddressSelected"
            />
          </div>

          <!-- Country -->
          <div class="form-group">
            <label class="form-label"
              >{{ $t("message.address_form.country") }} *</label
            >
            <select
              v-model="formData.country"
              class="form-input bordered"
              required
            >
              <option value="">{{
                $t("message.address_form.select_country")
              }}</option>
              <option
                v-for="country in countriesWithSelected"
                :key="country.code"
                :value="country.code"
              >
                {{ country.label }}
              </option>
            </select>
          </div>

          <!-- State + City (caché de Infoauto) -->
          <InfoautoStateCityFields
            :country="formData.country"
            :state.sync="formData.state"
            :city.sync="formData.city"
            group-class="form-group"
            label-class="form-label"
            input-class="form-input bordered"
          />

          <!-- Postal Code -->
          <div class="form-group">
            <label class="form-label"
              >{{ $t("message.address_form.postal_code") }} *</label
            >
            <input
              v-model="formData.postalCode"
              type="text"
              class="form-input bordered"
              :placeholder="$t('message.address_form.enter_postal_code')"
              required
            />
          </div>

          <!-- Address Type (tipo_3) -->
          <div class="form-group">
            <label class="form-label"
              >{{ $t("message.address_form.address_type") }} *</label
            >
            <select
              v-model="formData.tipo3"
              class="form-input bordered"
              required
            >
              <option value="">{{
                $t("message.address_form.select_type")
              }}</option>
              <option value="1">{{
                $t("message.address_form.billing")
              }}</option>
              <option value="2">{{
                $t("message.address_form.shipping")
              }}</option>
              <option value="3">{{ $t("message.address_form.both") }}</option>
            </select>
          </div>

          <!-- Status - Prominent Section -->
          <div
            class="status-section"
            :class="{ 'status-disabled': !addressEnabled }"
          >
            <div class="status-header">
              <svg
                v-if="!addressEnabled"
                class="status-icon-warning"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <svg
                v-else
                class="status-icon-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 class="status-title">
                {{ $t("message.address_form.address_status") }}
              </h3>
            </div>
            <label class="status-checkbox-label">
              <input
                v-model="addressEnabled"
                type="checkbox"
                class="status-checkbox-input"
              />
              <span class="status-text">
                <strong>{{
                  addressEnabled
                    ? $t("message.customer.enabled")
                    : $t("message.customer.disabled")
                }}</strong>
                <span class="status-description">
                  {{
                    addressEnabled
                      ? $t("message.address_form.address_active_description")
                      : $t("message.address_form.address_inactive_description")
                  }}
                </span>
              </span>
            </label>
          </div>

          <!-- Action Buttons -->
          <div class="modal-actions">
            <button type="button" @click="$emit('close')" class="btn-cancel">
              {{ $t("common.cancel") }}
            </button>
            <button type="submit" class="btn-save" :disabled="saving">
              {{ saving ? $t("common.saving") : $t("common.save_changes") }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import {
  updateAddressToCustomer,
  getInfoautoCountries
} from "@/api/store/Infoauto.js";
import GoogleAddressSearch from "@/components/global/GoogleAddressSearch.vue";
import InfoautoStateCityFields from "@/components/Infoauto/InfoautoStateCityFields.vue";

export default {
  name: "InfoautoEditAddressModal",
  components: {
    GoogleAddressSearch,
    InfoautoStateCityFields
  },
  props: {
    customerId: {
      type: String,
      required: true
    },
    address: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      error: null,
      countries: [],
      formData: {
        dir_id: "",
        name: "",
        street1: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
        tipo3: ""
      }
    };
  },
  computed: {
    addressEnabled: {
      get() {
        return this.formData.disabled === 0;
      },
      set(value) {
        this.formData.disabled = value ? 0 : 1;
      }
    },
    countriesWithSelected() {
      const selectedCountry = this.formData.country;
      const countryExists = this.countries.some(
        country => country.code === selectedCountry
      );
      if (!countryExists && selectedCountry) {
        return [
          ...this.countries,
          { code: selectedCountry, label: selectedCountry }
        ];
      }
      return this.countries;
    }
  },
  mounted() {
    this.initializeForm();
    this.loadCountries();
  },
  methods: {
    async loadCountries() {
      try {
        this.countries = await getInfoautoCountries();
      } catch (error) {
        console.error("Error loading countries:", error);
        this.$toast.error("Failed to load countries");
      }
    },
    initializeForm() {
      // Map the address prop to formData
      this.formData = {
        dir_id: this.address.infoautoAddressId,
        name: this.address.name || "",
        street1: this.address.street1 || "",
        city: this.address.city || "",
        state: this.address.state || "",
        postalCode: this.address.postalCode || "",
        country: this.address.country || "",
        tipo3: String(this.address.tipo3 || ""),
        disabled: this.address.disabled || 0
      };
    },
    handleGoogleAddressSelected(addressData) {
      console.log("=== Address selected from GoogleAddressSearch ===");
      console.log("Parsed address data:", JSON.stringify(addressData, null, 2));

      // Fill all the address fields from Google data
      if (addressData.street1) {
        this.formData.street1 = addressData.street1;
      }
      if (addressData.city) {
        this.formData.city = addressData.city;
      }
      if (addressData.state) {
        this.formData.state = addressData.state;
      }
      if (addressData.postal_code) {
        this.formData.postalCode = addressData.postal_code;
      }
      if (addressData.country) {
        this.formData.country = addressData.country;
      }

      console.log("Updated form fields:", {
        street1: this.formData.street1,
        city: this.formData.city,
        state: this.formData.state,
        postalCode: this.formData.postalCode,
        country: this.formData.country
      });
    },
    async saveAddress() {
      this.saving = true;
      this.error = null;

      try {
        // Prepare data for API
        const addressData = {
          dir_id: this.formData.dir_id,
          street1: this.formData.street1,
          city: this.formData.city,
          state: this.formData.state,
          postalCode: this.formData.postalCode,
          country: this.formData.country,
          tipo_3: this.formData.tipo3,
          disabled: this.formData.disabled
        };

        console.log("Updating address:", addressData);

        const response = await updateAddressToCustomer(
          this.customerId,
          addressData
        );

        console.log("Address updated successfully:", response);

        this.$emit("address-updated", response);
        this.$emit("close");
      } catch (error) {
        console.error("Error updating address:", error);
        this.error =
          error.response?.data?.message || "Failed to update address";
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped lang="postcss">
.modal-overlay {
  @apply fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50;
  padding: 1rem;
}

.modal-content {
  @apply bg-white rounded-lg shadow-xl w-full;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  @apply flex justify-between items-center p-6 border-b;
}

.modal-title {
  @apply text-xl font-bold text-gray-900;
}

.close-button {
  @apply text-gray-400 hover:text-gray-600 text-3xl font-bold leading-none;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}

.modal-body {
  @apply p-6;
}

.loading-container {
  @apply flex justify-center items-center py-12;
}

.error-message {
  @apply bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded;
}

.address-form {
  @apply space-y-4;
}

/* En este modal van apilados, no como celdas de grid */
.address-form ::v-deep .state-city-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.form-group,
::v-deep .form-group {
  @apply flex flex-col;
}

.form-label,
::v-deep .form-label {
  @apply text-sm font-medium text-gray-700 mb-1;
}

.form-input,
::v-deep .form-input {
  @apply w-full px-3 py-2 rounded-md;
}

.checkbox-label {
  @apply flex items-center space-x-2 cursor-pointer;
}

.checkbox-input {
  @apply w-4 h-4;
}

.disabled-warning {
  @apply bg-red-50 border-l-4 border-red-500 p-4 flex items-start gap-3;
}

.warning-icon {
  @apply w-6 h-6 text-red-500 flex-shrink-0 mt-1;
}

.disabled-warning strong {
  @apply text-red-800 text-lg;
}

.disabled-warning p {
  @apply text-red-700;
}

.status-section {
  @apply border-2 rounded-lg p-4 transition-all;
  border-color: #10b981;
  background: linear-gradient(to right, #f0fdf4, #dcfce7);
}

.status-section.status-disabled {
  border-color: #ef4444;
  background: linear-gradient(to right, #fef2f2, #fee2e2);
}

.status-header {
  @apply flex items-center gap-2 mb-3;
}

.status-title {
  @apply text-lg font-bold text-gray-800;
}

.status-icon-warning {
  @apply w-6 h-6 text-red-500;
}

.status-icon-success {
  @apply w-6 h-6 text-green-500;
}

.status-checkbox-label {
  @apply flex items-start gap-3 cursor-pointer;
}

.status-checkbox-input {
  @apply w-6 h-6 mt-1 cursor-pointer;
}

.status-text {
  @apply flex flex-col;
}

.status-text strong {
  @apply text-xl mb-1;
}

.status-text .status-description {
  @apply text-sm text-gray-600;
}

.modal-actions {
  @apply flex justify-end space-x-3 mt-6 pt-4 border-t;
}

.btn-cancel {
  @apply px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50;
}

.btn-save {
  @apply px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700;
}

.btn-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Encima de la cabecera fija del modal de orden (z-index 60), que tapaba el
   titulo y el boton de cerrar */
.modal-overlay {
  z-index: 70;
}

/* Movil: pantalla completa, con cabecera (y cerrar) y botones siempre a la
   vista; antes la X se iba con el scroll del formulario */
@media (max-width: 767px) {
  .modal-overlay {
    padding: 0;
  }

  .modal-content {
    height: 100%;
    max-height: none;
    border-radius: 0;
  }

  .modal-header {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 0.5rem 0.5rem 0.5rem 1rem;
    background: white;
  }

  .close-button {
    width: 44px;
    height: 44px;
  }

  .modal-body {
    padding: 1rem;
  }

  /* Selector largo: le gana al space-y del form (margen inferior) y el
     !important a la regla de <=390px del modal de orden, que lo dejaba al
     ancho del form en vez de llegar a los bordes */
  .modal-overlay .address-form > .modal-actions {
    position: sticky;
    bottom: 0;
    max-width: none !important;
    margin: 1.5rem -1rem -1rem;
    padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
    background: white;
  }

  .modal-actions button {
    flex: 1;
    min-height: 44px;
  }
}
</style>
